import { useEffect, useRef } from "react";
import { useNotify } from "react-admin";
import { useFormContext, useFormState, type FieldErrors } from "react-hook-form";

const BASE_LABELS: Record<string, string> = {
  Title: "Заголовок",
  Body: "Текст",
  Description: "Описание",
  Phrase: "Фраза",
  Subtitle: "Подзаголовок",
  ActionLabel: "Кнопка",
  Image: "Изображение",
  Video: "Видео/медиа",
  Filename: "Файл",
  Background: "Фон",
  BackgroundMusic: "Фоновая музыка",
  Icon: "Иконка",
  Phone: "Телефон",
  Route: "Маршрут / URL",
  GameUrl: "URL игры",
  Color: "Цвет",
  Emoji: "Emoji",
  Level: "Уровень",
  Type: "Тип",
  TypeKey: "Тип",
  ItemType: "Тип элемента",
  SectionKey: "Раздел",
  Category: "Категория",
  CategoryKey: "Категория",
  Latitude: "Широта",
  Longitude: "Долгота",
  DurationMin: "Длительность (мин)",
  SortOrder: "Порядок",
  PublishedAt: "Дата публикации",
  MediaType: "Тип медиа",
  OsmKey: "OSM key",
  Website: "Веб-сайт",
  Address: "Адрес",
  Level1Label: "Уровень 1",
  Level2Label: "Уровень 2",
  Level3Label: "Уровень 3",
  EmergencyCallWarningText: "Предупреждение о звонке",
  ChatFooterNoticeText: "Подпись в чате",
  SafetyPlanTemplate: "Шаблон плана безопасности",
  LegacyDiseaseId: "Legacy disease ID",
};

const LANG_RE = /^(.+?)(AZ|RU|EN)$/;

const isFieldError = (value: unknown): value is { type: string; message?: unknown } =>
  typeof value === "object" && value !== null && "type" in value;

const flattenErrorNames = (errors: FieldErrors, prefix = ""): string[] => {
  const names: string[] = [];
  for (const [key, value] of Object.entries(errors)) {
    if (!value || key === "root") continue;
    const name = prefix ? `${prefix}.${key}` : key;
    if (isFieldError(value)) {
      names.push(name);
    } else if (typeof value === "object") {
      names.push(...flattenErrorNames(value as FieldErrors, name));
    }
  }
  return names;
};

/** «TitleAZ» → «Заголовок (AZ)»; несколько языков одной группы склеиваются. */
const fieldLabel = (name: string): { base: string; langs: string[] } => {
  const leaf = name.split(".").pop() ?? name;
  const match = leaf.match(LANG_RE);
  if (match && match[1]) {
    return { base: match[1], langs: [match[2]] };
  }
  return { base: leaf, langs: [] };
};

const baseLabel = (base: string) => BASE_LABELS[base] ?? base;

const describeFields = (names: string[]): string[] => {
  const groups = new Map<string, { langs: Set<string>; order: number }>();
  names.forEach((name, order) => {
    const { base, langs } = fieldLabel(name);
    const existing = groups.get(base);
    if (existing) {
      langs.forEach((l) => existing.langs.add(l));
    } else {
      groups.set(base, { langs: new Set(langs), order });
    }
  });
  return [...groups.entries()]
    .sort((a, b) => a[1].order - b[1].order)
    .map(([base, { langs }]) =>
      langs.size > 0 ? `${baseLabel(base)} (${[...langs].join("/")})` : baseLabel(base),
    );
};

const MAX_FIELDS_IN_MESSAGE = 5;

/**
 * Заменяет стандартный тост «The form is not valid»: показывает,
 * какие именно поля не заполнены и на каком языке.
 */
export const InvalidFieldsNotifier = () => {
  const { control } = useFormContext();
  const { submitCount, errors } = useFormState({ control });
  const submitCountRef = useRef(submitCount);
  const notify = useNotify();

  useEffect(() => {
    if (submitCount <= submitCountRef.current) {
      submitCountRef.current = submitCount;
      return;
    }
    submitCountRef.current = submitCount;

    const serverError =
      typeof errors.root?.serverError?.message === "string"
        ? errors.root.serverError.message
        : undefined;
    if (serverError) {
      notify(serverError, { type: "error" });
      return;
    }

    const fields = describeFields(flattenErrorNames(errors));
    if (fields.length === 0) return;

    const shown = fields.slice(0, MAX_FIELDS_IN_MESSAGE);
    const rest = fields.length - shown.length;
    const suffix = rest > 0 ? ` и ещё ${rest}` : "";
    notify(`Не заполнено: ${shown.join(", ")}${suffix}`, {
      type: "error",
      autoHideDuration: 8000,
    });
  }, [errors, submitCount, notify]);

  return null;
};
